import { Group, Stack } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import i18n from '../../base-dictionary';
import { BrandedLoadingOverlay } from '../../components/loader/branded-loader';
import { PageLayout } from '../../components/page-layout/page-layout';
import { useGlobalStore } from '../../store/global-store/global-store';
import { ProcessStoreProvider } from '../../store/process-store/process-store-provider';
import { resetProcessStore } from '../../store/process-store/process-store.hooks';
import { secondaryBase, skyDark } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Icon } from '../../ui-library/icon/icon';
import { H2, Text } from '../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { getEventEdgeTaskDetailTitle, getProcessWorkflowTaskDetailTitle } from '../process/process.helpers';
import { useProcessScope } from '../process/process.hooks';
import { ViewType } from '../process/process.type';
import type { TaskRecord } from '../tasks-table/tasks-table.type';
import { TaskDetails } from '../work-periods/task-detail/task-details';
import { TaskSideSection } from '../work-periods/task-detail/task-side-section';
import { EventGraph } from './event-graph/event-graph';
import { ProcessV2FilterPanel } from './filters/process-v2-filter-panel';
import { ProcessGraph } from './process-graph/process-graph';
import { ProcessOverview } from './process-overview';
import { ProcessTabs } from './process-tabs';
import { useProcessViewData } from './process-v2.hooks';

// Create a separate component to use the context
const ProcessContent = ({
  expandTaskDetails,
  toggleExpandTaskDetails,
}: { expandTaskDetails: boolean; toggleExpandTaskDetails: () => void }) => {
  const [viewType, setViewType] = useState<ViewType>(ViewType.Count);

  // Use the restructured hook data
  const { left, right, isComparisonView, isLoading, isLoadingTasks } = useProcessViewData();

  // Helper function to render a main section (graph or event graph)
  const renderSection = (side: typeof left | typeof right) => {
    return side.scope.team === null && side.scope.workflow === null ? (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Stack align="center" justify="center" gap={8}>
          <Icon name="info" color={skyDark} size={36} />
          <Text weight="bold" size="large" color={skyDark}>
            {`Select a ${i18n.t('common.project')}`}
          </Text>
        </Stack>
      </div>
    ) : side.eventScope.showEventGraph ? (
      <EventGraph
        viewType={viewType}
        currentEventScope={side.eventScope}
        setCurrentEventScope={side.setEventScope}
        isRightGraph={side === right}
      />
    ) : (
      <ProcessGraph
        viewType={viewType}
        graphData={side.graphData}
        mapping={side.mapping}
        scope={side.scope}
        setScope={side.setScope}
        stages={side.stages}
        isRightGraph={side === right}
      />
    );
  };

  // Get tasks data for the task details panel
  const activeWorkflow = left.workflow || right.workflow;
  const activeEventEdge = left.eventScope.selectedEdgeId || right.eventScope.selectedEdgeId;
  const isWorkflowActive = Boolean(activeWorkflow?.mapping || activeEventEdge);
  const tasks = activeEventEdge
    ? left.eventTasks.length > 0
      ? left.eventTasks
      : right.eventTasks
    : left.tasks.length > 0
      ? left.tasks
      : right.tasks;

  const tasksTitle = activeWorkflow
    ? getProcessWorkflowTaskDetailTitle(activeWorkflow, activeWorkflow === right.workflow, isComparisonView)
    : activeEventEdge
      ? getEventEdgeTaskDetailTitle(
          activeEventEdge,
          activeEventEdge === right.eventScope.selectedEdgeId,
          isComparisonView,
        )
      : '';

  const memoizedLeftTaskTypes = useMemo(() => left.graphData?.task_types ?? [], [left.graphData?.task_types]);
  const memoizedRightTaskTypes = useMemo(() => right.graphData?.task_types ?? [], [right.graphData?.task_types]);

  return (
    <>
      <BrandedLoadingOverlay variant="colored" visible={isLoading} />
      {expandTaskDetails ? (
        <TaskDetails
          tasks={tasks as TaskRecord[]}
          title={`(${tasks.length}) ${tasksTitle}`}
          onBack={() => toggleExpandTaskDetails()}
        />
      ) : (
        <PageLayout
          controlBar={null}
          tabs={<ProcessTabs viewType={viewType} setViewType={setViewType} />}
          overview={
            isComparisonView
              ? undefined
              : {
                  title: `${i18n.t('navigation.process')} Overview`,
                  content: <ProcessOverview graphData={left.graphData} />,
                }
          }
          sideFilters={{
            content: (
              <ProcessV2FilterPanel leftTaskTypes={memoizedLeftTaskTypes} rightTaskTypes={memoizedRightTaskTypes} />
            ),
          }}
          mainSection={renderSection(left)}
          secondMainSection={isComparisonView ? renderSection(right) : undefined}
          sideSection={
            <TaskSideSection
              tasks={tasks as TaskRecord[]}
              categoryTitle={tasksTitle}
              onClose={() => {
                left.setWorkflow(null);
                right.setWorkflow(null);
                left.setEventScope({
                  ...left.eventScope,
                  selectedEdgeId: null,
                });
                right.setEventScope({
                  ...right.eventScope,
                  selectedEdgeId: null,
                });
              }}
              isLoading={isLoadingTasks}
              onExpandTaskDetails={toggleExpandTaskDetails}
            />
          }
          showSideSection={isWorkflowActive}
        />
      )}
    </>
  );
};

// Component to watch for portfolio changes and trigger store reset
const usePortfolioWatcher = () => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const prevPortfolioIdRef = useRef<string | null>(portfolio?.id || null);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (prevPortfolioIdRef.current !== null && portfolio?.id !== prevPortfolioIdRef.current) {
      // Reset the store
      resetProcessStore();

      // Force a re-render of the provider by changing its key
      setResetKey((prev) => prev + 1);
    }

    prevPortfolioIdRef.current = portfolio?.id || null;
  }, [portfolio?.id]);

  return { resetKey };
};

export function ProcessV2() {
  const { team, workflow, board, startDate, endDate, useWorkflow } = useProcessScope();

  // Get the reset key from the portfolio watcher
  const { resetKey } = usePortfolioWatcher();

  // Local state to store selected dates before applying
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(startDate ? dayjs(startDate).toDate() : null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(endDate ? dayjs(endDate).toDate() : null);
  const [isDatesDirty, setIsDatesDirty] = useState(false);
  const [expandTaskDetails, { toggle: toggleExpandTaskDetails }] = useDisclosure(false);

  // Local state for maintaining the current applied dates for the provider
  const [currentStartDate, setCurrentStartDate] = useState<string | null>(startDate);
  const [currentEndDate, setCurrentEndDate] = useState<string | null>(endDate);

  // Refs to track previous values to prevent unnecessary updates
  const prevStartDateRef = useRef(startDate);
  const prevEndDateRef = useRef(endDate);

  // Update local state when scope dates change, but only if they've actually changed
  useEffect(() => {
    const startDateChanged = startDate !== prevStartDateRef.current;
    const endDateChanged = endDate !== prevEndDateRef.current;

    if (startDateChanged || endDateChanged) {
      if (startDateChanged) {
        setSelectedStartDate(startDate ? dayjs(startDate).toDate() : null);
        setCurrentStartDate(startDate);
        prevStartDateRef.current = startDate;
      }

      if (endDateChanged) {
        setSelectedEndDate(endDate ? dayjs(endDate).toDate() : null);
        setCurrentEndDate(endDate);
        prevEndDateRef.current = endDate;
      }
    }
  }, [startDate, endDate]);

  // Handle date selection without immediately updating scope
  const handleDateChange = (value: any) => {
    const startDate = value[0] ? dayjs(value[0]).toDate() : null;
    const endDate = value[1] ? dayjs(value[1]).toDate() : null;
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setIsDatesDirty(true);
  };

  // Apply the selected dates to the scope when Apply button is clicked
  const handleApplyDates = () => {
    const formattedStartDate = selectedStartDate ? dayjs(selectedStartDate).toISOString() : null;
    let formattedEndDate = null;

    if (selectedEndDate) {
      const endDateRaw = dayjs(selectedEndDate);
      const isCurrentMonth = endDateRaw.isSame(dayjs(), 'month');
      formattedEndDate = isCurrentMonth ? dayjs().toISOString() : endDateRaw.endOf('month').toISOString();
    }

    // Update our local state for the Provider's initialScope
    setCurrentStartDate(formattedStartDate);
    setCurrentEndDate(formattedEndDate);

    setIsDatesDirty(false);
  };

  const header = (
    <Group>
      <Icon name="map" color={secondaryBase} size={36} />
      <H2>{`${i18n.t('navigation.process')} ${expandTaskDetails ? i18n.t('common.work_units') : ''}`}</H2>

      {!expandTaskDetails && (
        <>
          <MonthPickerInput
            type="range"
            radius="xl"
            style={{ width: '300px' }}
            leftSection={<Icon name="calendar_month" color={secondaryBase} size={16} />}
            leftSectionPointerEvents="none"
            placeholder="Pick date range"
            value={[selectedStartDate, selectedEndDate]}
            onChange={handleDateChange}
            maxDate={dayjs().toDate()}
            clearable
          />
          <Button
            onClick={handleApplyDates}
            variant="secondary"
            radius="xl"
            size="xs"
            disabled={!isDatesDirty}
            style={{ border: !isDatesDirty ? `1px solid ${skyDark}` : 'none' }}
          >
            Apply Dates
          </Button>
        </>
      )}
    </Group>
  );

  // Create a unique key for the ProcessStoreProvider to force remounting
  // Now also include the resetKey to force remount when portfolio changes
  const providerKey = useMemo(
    () =>
      `process-provider-${resetKey}-${useWorkflow ? 'workflow' : 'team'}-${useWorkflow ? workflow?.id || 'no-workflow' : team?.id || 'no-team'}-${board?.id || 'no-board'}`,
    [team?.id, board?.id, workflow?.id, useWorkflow, resetKey],
  );

  // Create stable references for the initial scopes to prevent unnecessary re-renders
  const initialScope = useMemo(
    () => ({
      startDate: currentStartDate,
      endDate: currentEndDate,
    }),
    [currentStartDate, currentEndDate],
  );

  const initialRightScope = useMemo(
    () => ({
      startDate: currentStartDate,
      endDate: currentEndDate,
    }),
    [currentStartDate, currentEndDate],
  );

  return (
    <ProcessStoreProvider key={providerKey} initialScope={initialScope} initialRightScope={initialRightScope}>
      <MenuDrivenContainer header={header}>
        <ProcessContent expandTaskDetails={expandTaskDetails} toggleExpandTaskDetails={toggleExpandTaskDetails} />
      </MenuDrivenContainer>
    </ProcessStoreProvider>
  );
}
